import { nextTick, reactive } from 'vue';
import { intersection } from 'lodash-es';
import { getUid, toggleGlobalListener } from '@orion.ui/orion';
import { SETTING_TYPE } from '@/typings/enums/SETTING_TYPE';
import { SIGNATURE_STATUS } from '@/typings/enums/SIGNATURE_STATUS';
import BaseSetupService from './BaseSetupService';
import router from '@/router/index.router';
import UserSettingApi from '@/api/UserSettingApi';
import type BaseEntity from '@/entity/BaseEntity';
import useNavigationGuardService from '@/services/NavigationGuardService';

type Props = ServiceProps<typeof BasePagedListSetupService.props>
type RawItemType = { id: number }
type ItemEntity = typeof BaseEntity<RawItemType>
type ItemEntityMapper = (entries: any[]) => BaseEntity<RawItemType>[]
export type ItemGeneric = RawItemType | ItemEntity | (() => ItemEntity)
type ItemType<T extends ItemGeneric> =
  T extends ItemEntity
    ? InstanceType<T>
    : T extends (() => ItemEntity)
      ? InstanceType<ReturnType<T>>
      : T

export default abstract class BasePagedListSetupService<
	P,
	T extends ItemGeneric,
> extends BaseSetupService<Props & P> {
	abstract get listTitle (): string
	abstract filterService: PagedListFilterServiceList;

	static readonly props? = {};

	readonly uid = getUid();

	private globalRefreshListenerUid = getUid();
	private listRouteName: string;
	private detailsRouteName: string;
	private _popable?: OrionModal | OrionAside;
	private readonly state = reactive({
		displaySearch: false,
		dataLoading: false,
		pageMounted: false,
	});

	protected refreshListCallback?: () => any;
	protected scrollTopOnRefresh = true;
	protected listBind: ListBind = {};
	protected readonly listState = reactive({
		layout: 'row',
		list: [],
		selected: [],
		total: 0,
		trackKey: 'id',
		itemType: 'document',
		usePaginationTop: false,
		cellClass: 'col-sm-6 col-lg-4',
		gridClass: 'row row--grid-sm',
		useLocalLoader: false,
	}) as PagedList<ItemType<T>>;

	protected get asideDetailsProps (): Record<string, any> { return {}; }
	protected get listItemEntity (): undefined | (T extends ItemEntity ? T : ItemEntityMapper | undefined) {
		return undefined;
	}

	get autoConnectListFilter () { return true; }
	get useLayoutAsideForDetails () { return true; }
	get permissionGuard (): undefined | PermissionGuard | PermissionGuard[] { return undefined; }
	get list () { return this.listState.list as ItemType<T>[]; }
	get listTotal () { return this.listState.total; }
	get listProps () { return this.listState; }
	get listItemType () { return this.listState.itemType; }
	get listPreferGridLayout () { return this.listState.layout === 'grid' || this.ui.onLayoutNavBottom; }
	get listLayoutIsGrid () { return this.listState.layout === 'grid'; }
	get listLayoutIsRow () { return this.listState.layout === 'row'; }
	get listSelectedItems () { return this.listState.selected as T[]; }
	get listHasActiveFilter () { return this.filterService.filterIsDifferentFromOriginal; }
	get dataLoading () { return this.state.dataLoading; }
	get pageMounted () { return this.state.pageMounted; }

	get displaySearch () { return this.state.displaySearch; }
	set displaySearch (val) { this.state.displaySearch = val; }

	get routeName () {
		return {
			listRouteName: this.listRouteName,
			detailsRouteName: this.detailsRouteName,
		};
	}

	get listPage () { return this.filterService.filter.page; }
	set listPage (val: OrionList.Props['page']) {
		this.filterService.applyWithRouterAsync();
	}

	get listHasAllItemsSelected () {
		return this.listSelectedItems.length && intersection(
			this.listSelectedItems.mapKey('id'),
			this.listState.list.mapKey('id'),
		).length === this.listState.list.length;
	}

	get selectedCanBeSigned () {
		if (!this.listSelectedItems) return [];
		return this.listSelectedItems.filter(x => (
			!x.document?.integratedDate &&
      x.document.signatureInfoStatus ^ SIGNATURE_STATUS.AgencySigned) === SIGNATURE_STATUS.None,
		);
	}


	constructor (props?: Props & P, _popable?: OrionModal | OrionAside) {
		super(props);
		this._popable = _popable;
		this.setRoutingData();

		nextTick(() => Object.assign(this.listState, this.listBind));
	}

	protected async onBeforeMountAsync () {
		if (this.self.isTempWorker() || !this.permissionGuard || useNavigationGuardService(this.permissionGuard)) {
			if (this.autoConnectListFilter) this.filterService.connect(this, this._popable);
		}
	}

	protected onMounted () {
		toggleGlobalListener(
			`${this.listRouteName}:refresh`,
			() => this.refreshListAsync(),
			{ uid: this.globalRefreshListenerUid },
		);

		this.state.pageMounted = true;
	}

	protected onUnmounted () {
		toggleGlobalListener(this.globalRefreshListenerUid);
		this.filterService.disconnect(this);
	}


	async hookBeforeApplyFilterAsync () {}

	setRoutingData () {
		this.listRouteName = String(router.currentRoute.value.name).replace(/Details$/, '');
		this.detailsRouteName = `${this.listRouteName}Details`;
	}

	async refreshListAsync (resetPagination = false) {
		if (this.scrollTopOnRefresh) window.scrollTo(0, 0);
		this.state.dataLoading = true;
		await this.filterService.applyAsync(resetPagination, this._popable);

		this.state.dataLoading = false;

		this.refreshListCallback?.();
	}

	setData (data: IPagedList<T>) {
		if (typeof this.listItemEntity === 'function') {
			/^class /.test(this.listItemEntity?.toString())
				? this.listState.list.empty().push(...(this.listItemEntity as ItemEntity).map(data.list as RawItemType[]))
				: this.listState.list.empty().push(...(this.listItemEntity as Function)(data.list as RawItemType[]));
		} else {
			this.listState.list.empty().push(...data.list);
		}

		this.listState.total = data.count;
	}

	listItemIsSelected (item: ItemType<T>) {
		return this.listState.selected.mapKey('id').includes(item.id);
	}

	selectListItem (item: ItemType<T>) {
		const index = this.listState.selected.findIndex(x => x.id === item.id);
		if (index < 0) {
			this.listState.selected.push(item);
		} else {
			this.listState.selected.splice(index, 1);
		}
	}

	selectAllListItems () {
		const selectedIds = this.listSelectedItems.mapKey('id').filterNil();
		this.listState.list.forEach((x) => {
			if (x.id && !selectedIds.includes(x.id)) {
				this.listState.selected.push(x);
			}
		});
	}

	emptySelectedItems () {
		this.listState.selected.empty();
	}

	unselectItemById (id: number) {
		const index = this.listState.selected.findIndex(x => x.id === id);
		if (index > -1) this.listState.selected.splice(index, 1);
	}

	toggleSelectAllListItems () {
		if (this.listHasAllItemsSelected) {
			this.emptySelectedItems();
		} else {
			this.selectAllListItems();
		}
	}

	async setListLayoutAsync (val: PagedList<T>['layout']) {
		this.listState.layout = val;

		await UserSettingApi.updateSetting({
			settingType: SETTING_TYPE.Layout,
			value: val === 'row' ? 'true' : 'false', // TODO: verif si 'grid' | 'row' | 'boolean'
		});
	}
}
